import axios, { AxiosError } from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../store/authStore'

// Configuração base da API
// Em produção com nginx, use URL vazia para URLs relativas
// Em desenvolvimento local, mantenha a URL do backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
const API_VERSION = 'v1'

export const apiClient = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api/${API_VERSION}` : `/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token JWT em todas as requisições
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().user?.token
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
      console.log(`🔑 Token adicionado ao header da requisição: ${config.method?.toUpperCase()} ${config.url}`)
    } else {
      console.warn(`⚠️ Token NÃO encontrado para: ${config.method?.toUpperCase()} ${config.url}`)
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para tratar erros de autenticação
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Não loga erros de rede quando offline
    if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
      console.log('📡 Offline - usando cache local')
      return Promise.reject({
        ...error,
        isOffline: true,
        message: 'Modo offline - dados do cache'
      })
    }

    if (error.response?.status === 401) {
      // Só redireciona e faz logout se NÃO for uma tentativa de login
      // Se for login, deixa o erro ser tratado pela página de login
      const isLoginRequest = error.config?.url?.includes('/auth/login')
      
      if (!isLoginRequest) {
        // Token inválido ou expirado - fazer logout
        useAuthStore.getState().logout()
      }
    }
    return Promise.reject(error)
  }
)

// Tipos de erro da API
export interface ApiError {
  code: string
  message: string
  details?: Array<{
    field: string
    message: string
  }>
}

// Função auxiliar para extrair mensagem de erro
export const getErrorMessage = (error: unknown): string => {
  // Se estiver offline, retorna vazio para não mostrar nada
  if (!navigator.onLine) {
    return ''
  }

  if (axios.isAxiosError(error)) {
    // Ignora erros de rede quando offline ou retorna vazio
    if (error.code === 'ERR_NETWORK') {
      return ''
    }

    const apiError = error.response?.data as ApiError
    if (apiError?.message) {
      return apiError.message
    }
    if (apiError?.details && apiError.details.length > 0) {
      return apiError.details.map(d => d.message).join(', ')
    }
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'Erro desconhecido'
}
